import {
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  Button,
  Box,
  Flex,
  Image,
  Grid,
  useToast,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from 'store/slices/authSlice';
import { ReactComponent as CloseIcon } from 'icons/cross.svg';
import { useLazyGetPaymentLinkQuery } from 'store/services/users';
import { useGetSubscriptionsQuery, useAddRaffleParticipationMutation } from 'store/services/api';
import { event, EVENTS } from 'utils/analytics';
import { useEffect } from 'react';
import { setInitialIndex as setRaffleInitialIndex } from 'store/slices/raffleSlice';
import { useLazyGetWeeklyRaffleQuery } from 'store/services/api';

import UpgradeToPremiumBanner from './UpgradeToPremiumBanner';

export default function CoinPurchaseSheet({ isOpen, onClose, onSuccess }) {
  const user = useSelector(selectUser);
  const toast = useToast();
  const dispatch = useDispatch();
  const selectedRaffle = useSelector(state => state.raffle.initialIndex);
  const { data: subscriptions } = useGetSubscriptionsQuery();
  const [getPaymentLink] = useLazyGetPaymentLinkQuery();
  const [addRaffleParticipation, { isSuccess, isError }] = useAddRaffleParticipationMutation();
  const [getRaffles, { data: weeklyRaffle }] = useLazyGetWeeklyRaffleQuery();

  useEffect(() => {
    getRaffles();
  }, []);

  const onCloseClick = () => {
    dispatch(setRaffleInitialIndex(null));
    onClose() && onClose();
  };

  const onButtonClick = () => {
    addRaffleParticipation({ raffleId: selectedRaffle || weeklyRaffle?.results[0]?.id });
  };

  const onBannerClick = async () => {
    if (!subscriptions || !subscriptions.length) return;

    if (user && user.subscribed) {
      toast({
        duration: 3000,
        isClosable: true,
        position: 'top',
        render: ({ onClose }) => {
          return (
            <Flex
              alignItems="center"
              justifyContent="center"
              p="5px 18px !important"
              backgroundColor="#111111"
              borderRadius="10px"
              gap="10px"
              w="min-content"
              whiteSpace="nowrap"
              margin="0 auto 10px"
              fontSize="14px"
              fontWeight="600"
              onClick={onClose}
            >
              <Box>Already Upgaraded</Box>
            </Flex>
          );
        },
      });
      return;
    }

    const selectedSubscription = subscriptions[0];
    event('Purchase', EVENTS.COIN_PURCHASE, 'get_coins', {
      subscription_id: selectedSubscription.id,
      amount: selectedSubscription?.unit_amount || 0,
      coins: selectedSubscription?.coins_amount || 0,
    });

    const response = await getPaymentLink(selectedSubscription.id);
    if (response.data?.url) {
      window.location.href = response.data.url;
    }
  };

  useEffect(() => {
    if (isSuccess) {
      toast({
        duration: 3000,
        isClosable: true,
        position: 'top',
        render: ({ onClose }) => {
          return (
            <Flex
              alignItems="center"
              justifyContent="center"
              p="5px 18px !important"
              backgroundColor="#111111"
              borderRadius="10px"
              gap="10px"
              w="min-content"
              whiteSpace="nowrap"
              margin="0 auto 10px"
              fontSize="14px"
              fontWeight="600"
              onClick={onClose}
            >
              <Box>Success</Box>
            </Flex>
          );
        },
      });

      dispatch(setRaffleInitialIndex(null));
      if (onSuccess) {
        onSuccess();
      }
      getRaffles();
      onClose();
    }

    if (isError) {
      toast({
        duration: 3000,
        isClosable: true,
        position: 'top',
        render: ({ onClose }) => {
          return (
            <Flex
              alignItems="center"
              justifyContent="center"
              p="5px 18px !important"
              backgroundColor="#111111"
              borderRadius="10px"
              gap="10px"
              w="min-content"
              whiteSpace="nowrap"
              margin="0 auto 10px"
              fontSize="14px"
              fontWeight="600"
              onClick={onClose}
            >
              <Box>Error</Box>
            </Flex>
          );
        },
      });
    }
  }, [isSuccess, isError]);

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onCloseClick} size="md">
      <DrawerOverlay />
      <DrawerContent borderTopRadius="8px" backgroundColor="rgba(22, 31, 40, 1)" overflow="hidden">
        <DrawerBody px="15px" w="100%" maxW="400px" mx="auto">
          <Grid gridTemplateColumns="48px 1fr 48px">
            <Flex
              h="48px"
              w="100%"
              justifyContent="center"
              alignItems="center"
              onClick={onCloseClick}
              style={{ cursor: 'pointer' }}
            >
              <CloseIcon />
            </Flex>
            <Flex fontSize="16px" fontWeight="700" textAlign="center" justifyContent="center" alignItems="center">
              ENTER RAFFLE
            </Flex>
            <Box />
          </Grid>
          <Flex gap="5px" alignItems="center" justifyContent="center" mt="16px">
            <Image src="/images/coin.png" alt="coin" w="48px" h="48px" objectFit="contain" objectPosition="center" />
            <Box
              fontSize="44px"
              fontWeight="400"
              lineHeight="1.2"
              fontFamily="'Fugaz One', cursive"
              textTransform="uppercase"
              position="relative"
              top="1px"
            >
              50
            </Box>
          </Flex>
          <Flex gap="12px" mt="24px" flexDirection="column" alignItems="center" justifyContent="center">
            <Button
              onClick={onButtonClick}
              variant="blue"
              isDisabled={user?.current_amount < 50 || weeklyRaffle?.results[0]?.is_participating}
              _disabled={{
                backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
                color: 'rgba(255, 255, 255, 0.5)',
              }}
              h="40px"
              px="20px"
            >
              {weeklyRaffle?.results[0]?.is_participating ? 'SUBSCRIBED' : 'BUY ENTRY'}
            </Button>
            {user?.current_amount < 50 && (
              <Flex
                fontSize="12px"
                fontWeight="700"
                color="rgba(221, 94, 94, 1)"
                w="100%"
                justifyContent="center"
                alignItems="center"
                onClick={onCloseClick}
                style={{ cursor: 'pointer' }}
              >
                NOT ENOUGH COINS
              </Flex>
            )}
          </Flex>
          <UpgradeToPremiumBanner onBannerClick={onBannerClick} isDisabled={user && user.subscribed} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
